import useScrollReveal from '../../hooks/useScrollReveal';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import MidCTABanner from '../../components/feature/MidCTABanner';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreasSection from '../../components/feature/ServiceAreasSection';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';
import { Link } from 'react-router-dom';

const services = [
  {
    name: 'Chain-Link Fencing',
    tagline: 'Security & Boundary Definition',
    image: 'https://public.readdy.ai/ai/img_res/6494ab90c2d7088c463249fcb6047572.jpg',
    benefits: ['Galvanized — rust resistant for decades', 'Custom heights, mesh gauges & coatings', 'Ideal for residential & commercial sites', 'Lowest maintenance fencing option'],
    desc: "Masters Fencing installs durable chain-link systems that deliver reliable boundary definition and security for homes and businesses across St. Mary's. Galvanized for rust resistance, built for Georgia's climate, and available in custom heights to fit any property.",
  },
  {
    name: 'Wood Privacy Fence',
    tagline: 'Natural Beauty & Complete Seclusion',
    image: 'https://readdy.ai/api/search-image?query=Exquisite%20handcrafted%20board-on-board%20cedar%20wood%20privacy%20fence%20in%20lush%20luxury%20backyard%2C%20perfectly%20uniform%20vertical%20boards%20with%20visible%20natural%20wood%20grain%2C%20deep%20warm%20honey%20amber%20tones%20in%20afternoon%20sunlight%2C%20manicured%20emerald%20lawn%2C%20colorful%20flower%20garden%20at%20base%2C%20white%20Southern%20home%20partially%20visible%2C%20premium%20lifestyle%20real%20estate%20photography%2C%20Georgia%20residential%20neighborhood&width=960&height=620&seq=mf_v3_svc2&orientation=landscape',
    benefits: ['Board-on-board cedar & pressure-treated pine', 'True full-coverage privacy design', 'Custom heights, picket styles & staining', 'Meaningfully increases property value'],
    desc: "We craft custom wood fences that enhance privacy, add timeless natural beauty, and deliver lasting property value. Our skilled carpenters use premium lumber and precision techniques to build cedar and pine fencing that performs and looks exceptional throughout St. Mary's, GA.",
  },
  {
    name: 'Steel Fence',
    tagline: 'Architectural Strength & Presence',
    image: 'https://readdy.ai/api/search-image?query=Imposing%20matte%20black%20powder-coated%20ornamental%20steel%20fence%20with%20decorative%20spear-top%20finials%20at%20prestigious%20Southern%20estate%2C%20strong%20architectural%20iron%20fence%20casting%20long%20shadows%20on%20manicured%20lawn%2C%20warm%20golden%20sunset%20backlighting%20dramatic%20silhouette%2C%20luxury%20home%20architecture%20blurred%20behind%2C%20premium%20estate%20property%20photography&width=960&height=620&seq=mf_v3_svc3&orientation=landscape',
    benefits: ['Premium powder-coated finish', 'Ornamental & industrial styles available', 'Weather-hardened — built to last decades', 'Ideal for high-security applications'],
    desc: "Steel fencing from Masters Fencing delivers uncompromising strength, architectural presence, and modern elegance. Available in ornamental and industrial styles for residential gates and commercial perimeters — maintenance-friendly, durable, and built to perform across St. Mary's, GA.",
  },
  {
    name: 'Vinyl Fence',
    tagline: 'Zero Maintenance, Maximum Impact',
    image: 'https://readdy.ai/api/search-image?query=Immaculate%20bright%20white%20vinyl%20privacy%20fence%20in%20expansive%20luxury%20backyard%2C%20perfectly%20smooth%20clean%20surface%20reflecting%20sunlight%2C%20aligned%20posts%20and%20rails%2C%20brilliant%20vivid%20green%20grass%2C%20modern%20upscale%20Georgia%20neighborhood%20home%2C%20fresh%20and%20pristine%20real%20estate%20presentation%2C%20professional%20wide%20angle%20photography&width=960&height=620&seq=mf_v3_svc4&orientation=landscape',
    benefits: ['100% rot, fade & UV resistant', 'Privacy, picket & ranch styles', 'Never needs painting or staining', 'Withstands Georgia humidity & heat'],
    desc: "Vinyl fencing from Masters Fencing is the ultimate low-maintenance solution — resistant to rot, UV fading, and Georgia's demanding climate. Available in privacy, picket, and ranch styles, our vinyl installations look pristine for decades without the upkeep of traditional materials.",
  },
  {
    name: 'Aluminum Fence',
    tagline: 'Timeless Elegance, Rust-Free Durability',
    image: 'https://readdy.ai/api/search-image?query=Refined%20dark%20bronze%20aluminum%20decorative%20architectural%20fence%20framing%20an%20upscale%20pool%20area%20at%20luxury%20Southern%20estate%2C%20elegant%20vertical%20bar%20fence%20with%20decorative%20finials%2C%20crystal%20blue%20pool%20water%20reflections%2C%20resort-style%20landscaping%20with%20tropical%20plants%2C%20warm%20afternoon%20ambient%20light%2C%20high-end%20property%20photography&width=960&height=620&seq=mf_v3_svc5&orientation=landscape',
    benefits: ['Never rusts or corrodes', 'Pool-safe & ASTM certified', 'Decorative & architectural styles', 'Fully customizable height & color'],
    desc: "Aluminum fencing combines refined elegance with lasting strength — rust-free, fully customizable, and ideal for pools, estates, and properties where curb appeal and durability are both non-negotiable. Our aluminum installations across St. Mary's are built to impress for a lifetime.",
  },
];

const ServicesPage: React.FC = () => {
  useScrollReveal();

  return (
    <div className="bg-[#FAF8F5]">
      <Navbar />
      <main>
        <HeroSection
          label="Fencing Services · Camden County, GA"
          headline="Five Premium Fencing Solutions for Every Property"
          subheadline="From classic wood privacy fencing to ornamental steel and zero-maintenance vinyl — expert installation for every style and budget across St. Mary's, Georgia."
          bgImage="https://readdy.ai/api/search-image?query=Dramatic%20close-up%20view%20of%20three%20premium%20fence%20types%20side%20by%20side%2C%20ornamental%20black%20steel%2C%20rich%20warm%20cedar%20wood%2C%20and%20crisp%20white%20vinyl%20panels%2C%20beautiful%20natural%20light%20from%20left%20side%2C%20professional%20product%20comparison%20photography%2C%20luxury%20construction%20materials%2C%20sharp%20detail%2C%20premium%20contractor%20quality%20Georgia%20fencing&width=1920&height=1080&seq=mf_v3_hero_services&orientation=landscape"
          primaryCTA="Request a Consultation"
        />
        <TrustBar />

        {/* Services detail section */}
        <section className="relative bg-[#FAF8F5]" data-product-shop>
          <div className="section-py max-w-7xl mx-auto px-6">
            <div className="text-center mb-16 reveal">
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="gold-bar" />
                <span className="text-[#C4A84F] text-[11px] tracking-[0.38em] uppercase font-sans font-medium">What We Offer</span>
                <span className="gold-bar" />
              </div>
              <h2 className="font-serif text-[3.2rem] md:text-[4.5rem] text-[#0D0C0B] leading-[0.95] mb-5">
                Complete Fencing Services<br />in St. Mary's, GA
              </h2>
              <p className="text-[#0D0C0B]/48 text-base font-sans max-w-xl mx-auto leading-relaxed">
                Each service is tailored to your property's specific needs — we guide you through every option and ensure the result exceeds your expectations.
              </p>
            </div>

            <div className="space-y-4">
              {services.map((s, i) => (
                <article
                  key={s.name}
                  className={`reveal grid grid-cols-1 lg:grid-cols-2 overflow-hidden border border-[#0D0C0B]/6 group`}
                >
                  <div className={`relative h-72 lg:h-auto min-h-[420px] overflow-hidden img-zoom ${i % 2 !== 0 ? 'lg:order-2' : ''}`}>
                    <img
                      src={s.image}
                      alt={`${s.name} installation by Masters Fencing St. Mary's Georgia`}
                      title={`${s.name} — Masters Fencing`}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-${i % 2 === 0 ? 'r' : 'l'} from-transparent to-[#0D0C0B]/20 group-hover:to-[#0D0C0B]/35 transition-all duration-500`} />
                    {/* Service number */}
                    <div className="absolute bottom-5 right-5 font-serif text-5xl text-white/12 leading-none select-none">
                      0{i + 1}
                    </div>
                  </div>

                  <div className={`bg-white p-10 lg:p-16 flex flex-col justify-center ${i % 2 !== 0 ? 'lg:order-1' : ''}`}>
                    <div className="w-10 h-[2px] bg-[#C4A84F] mb-6" />
                    <h3 className="font-serif text-[2.5rem] md:text-[3rem] text-[#0D0C0B] mb-3 leading-[0.95]">
                      {s.name}
                    </h3>
                    <p className="text-[#C4A84F] text-[10px] tracking-[0.32em] uppercase font-sans font-semibold mb-6">
                      {s.tagline}
                    </p>
                    <p className="text-[#0D0C0B]/55 text-sm leading-relaxed font-sans mb-9">{s.desc}</p>
                    <ul className="grid grid-cols-2 gap-3 mb-10">
                      {s.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-sm text-[#0D0C0B]/60 font-sans leading-snug">
                          <i className="fa-solid fa-check text-[#C4A84F] text-xs shrink-0 mt-0.5" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href="#estimate"
                        className="inline-flex items-center justify-center gap-2 bg-[#C4A84F] text-[#0D0C0B] text-[11px] font-bold tracking-[0.16em] uppercase px-7 py-3.5 hover:bg-[#D4BC78] transition-all self-start whitespace-nowrap"
                      >
                        Get a Quote <i className="fa-solid fa-arrow-right text-[10px]" />
                      </a>
                      <Link
                        to="/locations"
                        className="inline-flex items-center justify-center gap-2 border border-[#0D0C0B]/12 text-[#0D0C0B]/55 text-[11px] font-medium tracking-wider uppercase px-7 py-3.5 hover:border-[#C4A84F] hover:text-[#C4A84F] transition-all self-start whitespace-nowrap"
                      >
                        Our Service Areas
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <MidCTABanner
          headline="Every Material. One Expert Team."
          sub="Not sure which fence type is right for your property? We'll walk you through every option — free, no obligation."
          cta="Request a Free Estimate"
        />
        <AIAnswerBlock
          focusTitle="Expert Fencing for Every Property Type"
          focusDescription="Whether you need a secure chain-link system for a commercial lot, a privacy wood fence for your backyard, or a decorative aluminum fence to elevate curb appeal — Masters Fencing has the 20+ years of local expertise, materials, and craftsmanship to deliver exactly what your St. Mary's property deserves."
        />
        <ProcessSection />
        <WhyChooseUs />
        <ServiceAreasSection />
        <TestimonialsSection />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
